import { useState, useMemo } from "react";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { IDS } from "@/lib/testIds";
import { Upload, DownloadSimple, Check, Warning, ClipboardText } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function ImportCenter() {
  const [kind, setKind] = useState("items");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  // Omitted-items review panel state
  const [omitted, setOmitted] = useState([]); // [{product_id, product_name, category, pack_size, current_qty, status}]
  const [decisions, setDecisions] = useState({}); // {product_id: "keep_active"|"deactivate"|"review_later"}
  const [omittedApplying, setOmittedApplying] = useState(false);
  const [omittedResult, setOmittedResult] = useState(null);

  const previewFile = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    try {
      const { data } = await api.post("/io/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const sheets = Object.keys(data.sheets || {});
      setSelectedSheet(sheets[0] || "");
      setResult(null); setOmitted([]); setDecisions({}); setOmittedResult(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const commit = async () => {
    if (!preview) return;
    const sh = preview.sheets[selectedSheet];
    setCommitting(true);
    try {
      const { data } = await api.post("/io/import/commit", {
        kind, format: preview.format, sheet: selectedSheet,
        header: sh.header, rows: sh.rows, upsert: true,
      });
      setResult(data);
      toast.success(`Import complete: +${data.created} created, ~${data.updated} updated`);
      // Fetch omitted items only for dry-goods import (kind=items)
      if (kind === "items") {
        const H = sh.header.map((h) => String(h).toLowerCase());
        const pidIdx = H.findIndex((h) => h === "product id" || h === "itemid");
        const pids = pidIdx >= 0 ? sh.rows.map((r) => r[pidIdx]).filter((x) => x !== null && x !== undefined && x !== "") : [];
        const { data: om } = await api.post("/io/import/omitted-preview", { kind: "items", product_ids_in_file: pids });
        setOmitted(om.omitted || []);
        // default every omitted item to review_later
        const d = {}; (om.omitted || []).forEach((o) => { d[o.product_id] = "review_later"; });
        setDecisions(d);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  };

  const applyOmitted = async () => {
    const decs = omitted.map((o) => ({ product_id: o.product_id, action: decisions[o.product_id] || "review_later" }));
    const deacs = decs.filter((d) => d.action === "deactivate").length;
    if (deacs > 0 && !window.confirm(`Confirm deactivation of ${deacs} item(s)? Inventory, QR codes, transactions and count history are preserved. This can be undone from Admin → Audit.`)) return;
    setOmittedApplying(true);
    try {
      const { data } = await api.post("/io/import/omitted-actions", {
        decisions: decs, confirm_deactivate: deacs > 0,
      });
      setOmittedResult(data.results);
      toast.success(`Applied: ${data.results.deactivated} deactivated · ${data.results.kept} kept · ${data.results.review_later} review later`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOmittedApplying(false); }
  };

  const setDec = (pid, action) => setDecisions((s) => ({ ...s, [pid]: action }));

  const sheet = preview && selectedSheet ? preview.sheets[selectedSheet] : null;
  const omittedCounts = useMemo(() => {
    const c = { keep_active: 0, deactivate: 0, review_later: 0 };
    omitted.forEach((o) => { c[decisions[o.product_id] || "review_later"] += 1; });
    return c;
  }, [omitted, decisions]);

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <Link to="/admin" className="text-xs text-muted-foreground hover:text-primary">← Admin</Link>
      <div className="my-3">
        <div className="label-tech mb-1">Data Ingest</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Import Center</h1>
      </div>

      <div className="card-flat border-border p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr_auto_auto] gap-3">
          <select data-testid={IDS.importKind} value={kind} onChange={(e) => setKind(e.target.value)}
            className="h-11 px-2 border border-border bg-background rounded-sm text-sm">
            <option value="items">Items (Dry Goods)</option>
            <option value="meal_codes">Meal / Diet Codes</option>
            <option value="locations">Locations</option>
          </select>
          <input data-testid={IDS.importFile} type="file" accept=".xlsx,.xlsm,.csv"
            key={file ? file.name : "empty"}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="h-11 px-2 border border-border bg-background rounded-sm text-sm" />
          <button data-testid={IDS.importPreview} onClick={previewFile}
            className="btn-hard h-11 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white flex items-center gap-1">
            <Upload size={14} weight="bold" /> Preview
          </button>
          {(file || preview) && (
            <button
              type="button"
              data-testid="import-clear"
              onClick={() => { setFile(null); setPreview(null); setSelectedSheet(""); setResult(null); setOmitted([]); setDecisions({}); setOmittedResult(null); }}
              className="btn-hard h-11 px-3 border border-border font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-muted"
            >
              Clear File
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <a href={`${BACKEND_URL}/api/io/templates/items`} className="flex items-center gap-1 text-primary hover:underline"><DownloadSimple size={12} /> Dry Goods Template</a>
          <a href={`${BACKEND_URL}/api/io/templates/meal_codes`} className="flex items-center gap-1 text-primary hover:underline"><DownloadSimple size={12} /> Meals Template</a>
          <a href={`${BACKEND_URL}/api/io/templates/locations`} className="flex items-center gap-1 text-primary hover:underline"><DownloadSimple size={12} /> Locations Template</a>
        </div>
      </div>

      {preview && (
        <div className="card-flat border-border overflow-hidden mb-4">
          <div className="p-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="label-tech mb-1">Preview</div>
              <div className="text-sm font-mono">Format: {preview.format} · Sheets: {Object.keys(preview.sheets).length}</div>
            </div>
            <div className="flex items-center gap-2">
              {Object.keys(preview.sheets).length > 1 && (
                <select value={selectedSheet} onChange={(e) => setSelectedSheet(e.target.value)}
                  className="h-9 px-2 border border-border bg-background rounded-sm text-xs">
                  {Object.keys(preview.sheets).map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              )}
              <button data-testid={IDS.importCommit} onClick={commit} disabled={committing}
                className="btn-hard h-9 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-60 flex items-center gap-1">
                <Check size={14} weight="bold" /> {committing ? "Importing…" : "Commit Import"}
              </button>
            </div>
          </div>
          {sheet && (
            <div className="overflow-auto max-h-[40vh]">
              <table className="w-full text-xs font-mono">
                <thead className="bg-muted/50 border-b border-border sticky top-0">
                  <tr>{sheet.header.map((h, i) => (<th key={`h-${i}-${h || "col"}`} className="text-left px-2 py-1.5 label-tech">{h}</th>))}</tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sheet.rows.slice(0, 50).map((r, i) => (
                    <tr key={`row-${i}-${String(r[0] ?? "")}`} className="hover:bg-muted/30">
                      {sheet.header.map((h, j) => (<td key={`c-${i}-${j}-${h || j}`} className="px-2 py-1 whitespace-nowrap">{String(r[j] ?? "")}</td>))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 text-xs text-muted-foreground font-mono border-t border-border">Showing {Math.min(50, sheet.rows.length)} of {sheet.total} rows.</div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="card-flat border-primary border-2 p-4 mb-4">
          <div className="label-tech mb-2 text-primary">Import Complete</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div><div className="text-xs text-muted-foreground">Created</div><div className="font-display font-black text-2xl">{result.created}</div></div>
            <div><div className="text-xs text-muted-foreground">Updated</div><div className="font-display font-black text-2xl">{result.updated}</div></div>
            <div><div className="text-xs text-muted-foreground">Skipped</div><div className="font-display font-black text-2xl">{result.skipped}</div></div>
            <div><div className="text-xs text-muted-foreground">Omitted (in DB, not in file)</div><div className="font-display font-black text-2xl">{omitted.length}</div></div>
            <div><div className="text-xs text-muted-foreground">Errors</div><div className="font-display font-black text-2xl text-destructive">{result.errors?.length || 0}</div></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 border border-destructive/50 rounded-sm p-2 max-h-40 overflow-auto">
              <div className="label-tech mb-1"><Warning size={12} className="inline" /> Exception report</div>
              {result.errors.slice(0, 20).map((e, i) => (<div key={`err-${i}-${(e.error || "").slice(0, 24)}`} className="text-xs font-mono">{e.error}</div>))}
            </div>
          )}
        </div>
      )}

      {/* ---- Omitted items review panel ---- */}
      {omitted.length > 0 && (
        <div className="card-flat border-yellow-500 border-2 p-4 mb-4" data-testid="omitted-review-panel">
          <div className="flex items-start gap-3 mb-3">
            <ClipboardText size={20} weight="bold" className="text-yellow-500 mt-0.5" />
            <div className="flex-1">
              <div className="label-tech text-yellow-500 mb-1">Existing items NOT present in uploaded file · {omitted.length}</div>
              <div className="text-xs text-muted-foreground">These items already exist in the database but were NOT in the workbook you just uploaded. Nothing was auto-deleted. Choose an action per item. Default: Review Later.</div>
              <div className="mt-2 text-[11px] font-mono">
                Selected: <span className="text-success">{omittedCounts.keep_active} keep</span> · <span className="text-destructive">{omittedCounts.deactivate} deactivate</span> · <span className="text-muted-foreground">{omittedCounts.review_later} review later</span>
              </div>
            </div>
            <button data-testid="omitted-apply" onClick={applyOmitted} disabled={omittedApplying}
              className="btn-hard h-10 px-4 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 disabled:opacity-50">
              {omittedApplying ? "Applying…" : "Apply Decisions"}
            </button>
          </div>

          <div className="max-h-[45vh] overflow-auto border border-border rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 border-b border-border sticky top-0 z-10">
                <tr className="text-left label-tech">
                  <th className="px-2 py-1.5">Product ID</th>
                  <th className="px-2 py-1.5">Product Name</th>
                  <th className="px-2 py-1.5">Category</th>
                  <th className="px-2 py-1.5">Pack Size</th>
                  <th className="px-2 py-1.5 text-right">On Hand</th>
                  <th className="px-2 py-1.5 text-center">Status</th>
                  <th className="px-2 py-1.5">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {omitted.map((o) => (
                  <tr key={o.product_id} data-testid={`omitted-row-${o.product_id}`}>
                    <td className="px-2 py-1.5 font-mono">{o.product_id}</td>
                    <td className="px-2 py-1.5 truncate max-w-[26ch]">{o.product_name}</td>
                    <td className="px-2 py-1.5">{o.category || <span className="text-muted-foreground italic">—</span>}</td>
                    <td className="px-2 py-1.5 font-mono">{o.pack_size}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{Number(o.current_qty || 0).toFixed(0)}</td>
                    <td className="px-2 py-1.5 text-center text-[10px] font-bold uppercase tracking-widest text-success">{o.status}</td>
                    <td className="px-2 py-1.5">
                      <select value={decisions[o.product_id] || "review_later"} onChange={(e) => setDec(o.product_id, e.target.value)}
                        data-testid={`omitted-decision-${o.product_id}`}
                        className="h-8 px-2 border border-border bg-background rounded-sm text-xs">
                        <option value="review_later">Review Later</option>
                        <option value="keep_active">Keep Active</option>
                        <option value="deactivate">Deactivate</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {omittedResult && (
            <div className="mt-3 p-3 border border-primary/50 rounded-sm text-xs font-mono">
              Applied: <span className="text-success">{omittedResult.kept} kept</span> · <span className="text-destructive">{omittedResult.deactivated} deactivated</span> · <span className="text-muted-foreground">{omittedResult.review_later} review later</span>
              {omittedResult.not_found > 0 && <span className="text-yellow-500"> · {omittedResult.not_found} not-found</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
